import { useEffect, useState, useRef, useCallback } from 'react';
import * as React from 'react';
import styled, { useTheme } from 'styled-components';
import Text12 from '@sdk/components/ui/typography/Text12';
import StyledSvg from '@sdk/components/ui/StyledSvg';
import Margin from '@sdk/components/ui/spacing/Margin';
import CloseIcon from '@sdk/components/ui/CloseIcon';
import { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import { SaveClientFlags } from '@sdk/authentication/types';
import Href from '@sdk/components/ui/Href';
import gettext from '@sdk/lang/gettext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import useAnalytics from '@sdk/analytics/useAnalytics';
import { AnalyticsEvents } from '@sdk/analytics/types';
import Row from '@sdk/components/ui/spacing/Row';

import BulbIcon from '../../assets/svg/UI/bulb.svg';
import shouldShowABTest from '../../authentication/helpers/shouldShowABTest';

const LEARN_MORE_URL = 'https://support.getsling.com/en/articles/4683228';
const COSTS_SAVE_KEY = 'costsSaveBannerDismissed';

enum DismissedOptions {
  onTrial = 'onTrial',
  afterTrial = 'afterTrial',
}

const Container = styled(Row)`
  align-items: center;
  padding: 0 21px;
`;

type Props = {
  user: AuthUserType;
  saveClientFlags: SaveClientFlags;
};

const CostsSaveBanner: React.FC<React.PropsWithChildren<Props>> = ({ user, saveClientFlags }) => {
  const [isOpen, setIsOpen] = useState(false);
  const closedManuallyRef = useRef(false);
  const theme = useTheme();
  const trialDaysLeft = user?.org?.trialDaysLeft;
  const { clientFlags } = user.settings;
  const { subscriptionStartDate, subscriptionLevel, premiumTrialStartDate, id } = user.org;
  const { track } = useAnalytics({
    userId: user.id,
    email: user.email,
    orgId: user.org.id,
    toastRestaurantGuids: user.org.toastRestaurantGuids,
  });

  const canDisplayBanner = useCallback((): boolean => {
    if (
      closedManuallyRef.current ||
      !shouldShowABTest(id) ||
      !user.isManager ||
      !clientFlags.canShowOTCostsBanner ||
      subscriptionStartDate
    ) {
      return false;
    }

    const dismissedOption = clientFlags[COSTS_SAVE_KEY];

    if (trialDaysLeft && !dismissedOption && subscriptionLevel) {
      return true;
    }

    if (
      !trialDaysLeft &&
      dismissedOption !== DismissedOptions.afterTrial &&
      !subscriptionLevel &&
      premiumTrialStartDate
    ) {
      return true;
    }

    return false;
  }, [
    id,
    clientFlags,
    premiumTrialStartDate,
    subscriptionLevel,
    subscriptionStartDate,
    trialDaysLeft,
    user.isManager,
  ]);

  useEffect(() => {
    if (canDisplayBanner()) {
      setIsOpen(true);
    }
  }, [canDisplayBanner]);

  const closeModal = (): void => {
    closedManuallyRef.current = true;
    setIsOpen(false);
    const dismissOption =
      !trialDaysLeft || !subscriptionLevel ? DismissedOptions.afterTrial : DismissedOptions.onTrial;

    const flags = {
      [COSTS_SAVE_KEY]: dismissOption,
    };

    saveClientFlags({ flags });
  };

  const onCloseClick = (): void => {
    track(AnalyticsEvents.costsSaveInfoDismissed);
    closeModal();
  };

  const onLearnMoreClick = (): void => {
    track(AnalyticsEvents.costsSaveInfoLearnMoreClicked);
    closeModal();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Container>
      <Margin right={12}>
        <div style={{ display: 'flex' }}>
          <StyledSvg svg={BulbIcon} color={theme.colors.gray} />
        </div>
      </Margin>
      <Text12 bold>
        {gettext('Our customers save up to 8% in overtime costs each week.')}{' '}
        <Href
          bold
          fontSize={12}
          themeColor={ThemeColorType.primary}
          href={LEARN_MORE_URL}
          target="_blank"
          onClick={onLearnMoreClick}>
          {gettext('Learn more')}
        </Href>
      </Text12>
      <Margin left={12}>
        <CloseIcon onClick={onCloseClick} />
      </Margin>
    </Container>
  );
};

export default CostsSaveBanner;
